import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { cx } from 'class-variance-authority';

import { pipe } from 'fp-ts/lib/function';
import {
  fromPredicate,
  map,
  getOrElse,
  toUndefined,
  fromNullable,
} from 'fp-ts/lib/Option';
import { head, map as mapA } from 'fp-ts/lib/Array';

import { ClubGuideListItem, ClubOfferListItem } from '@oresundsbron/api';

import {
  Box,
  PageFilter,
  Card,
  Typography,
  Grid,
  Pagination,
} from '@oresundsbron/bridge-ui';

import {
  useCategories,
  useClubArticles,
  useLocations,
  useClubParams,
} from '~/hooks';

import { setUserCode } from '~/stores/auth';

import { DefaultLink } from '~/components/Links/DefaultLink';
import { useWindowSize } from '../account/hooks/useWindowSize';

import LoadingArticles from './LoadingArticles';
import JubileeBow from './components/JubileeBow';
import { useLinksV2 } from '~/hooks/useLinksV2';

const _showAllValue = 'show-all';
const _pageParam = 'page';
const _categoryParam = 'category';
const _countryParam = 'country';
const _defaultPage = '1';

export const Club = () => {
  const router = useRouter();
  const [categoryQuery] = useCategories('club-categories');
  const locationQuery = useLocations(router.locale || '');
  const clubList = useRef<HTMLDivElement>();
  const { isMobile } = useWindowSize();
  const [scrollIntoViewEnabled, setScrollIntoView] = useState(false);
  const { items, categoryCount, pages, isFetching } = useClubArticles();
  const { t } = useTranslation(['club', 'common'], { nsMode: 'fallback' });
  const { getLink } = useLinksV2();

  const {
    currentCategoryParam,
    currentCountryParam,
    currentPageParam,
    setQueryParams,
    removeQueryParams,
  } = useClubParams();

  const handleFilterChange = (value: string) => {
    setScrollIntoView(true);
    if (value === _showAllValue) {
      removeQueryParams([_categoryParam, _pageParam]);
    } else {
      setQueryParams([
        { key: _categoryParam, value: value },
        { key: _pageParam, value: _defaultPage },
      ]);
    }
  };

  const handleCountryChange = (value: string) => {
    value === _showAllValue
      ? removeQueryParams([_countryParam, _pageParam])
      : setQueryParams([
          { key: _countryParam, value },
          { key: _pageParam, value: '1' },
        ]);
  };

  //Set user code used for light login on club offers
  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (
      router.query.acCustomerNo &&
      typeof router.query.acCustomerNo === 'string'
    ) {
      setUserCode(router.query.acCustomerNo);
    }
  }, [router, router.isReady]);

  useEffect(() => {
    if (scrollIntoViewEnabled) {
      clubList.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [items, currentPageParam, scrollIntoViewEnabled]);

  const filters = useMemo(() => {
    if (!categoryQuery.data) {
      return [];
    }

    return [
      {
        key: _showAllValue,
        label: t('filter.showAll'),
        count: 0,
      },
      ...categoryQuery.data.items.map(({ externalName, sys }) => ({
        key: sys.id,
        label: externalName,
        count: categoryCount[sys.id] || 0,
      })),
    ];
  }, [categoryCount, categoryQuery.data, t]);

  const locations = useMemo(
    () =>
      pipe(
        locationQuery.data,
        fromNullable,
        map((l) => l.items),
        map(mapA((loc) => ({ label: loc.externalName, value: loc.sys.id }))),
        map((arr) => [
          {
            value: _showAllValue,
            label: t('filter.showAll'),
          },
          ...arr,
        ]),
        toUndefined
      ),
    [locationQuery, t]
  );

  const CategoryAndLocation = ({
    category,
    location,
  }: {
    category: { externalName: string };
    location: { externalName: string };
  }) => (
    <div className="mb-2 flex items-center gap-1 text-base text-neutral-600">
      {category ? (
        <Typography as="span" weight="medium">
          {category.externalName}
        </Typography>
      ) : null}
      {category && location && (
        <Typography as="span" weight="bold" aria-hidden="true">
          &middot;
        </Typography>
      )}
      {location && <Typography as="span">{location.externalName}</Typography>}
    </div>
  );

  const todayDate = new Date().setHours(0, 0, 0, 0);

  const isValidVoucher = useCallback(
    (voucherValidUntilDate: string) => {
      return new Date(voucherValidUntilDate).setHours(0, 0, 0, 0) >= todayDate;
    },
    [todayDate]
  );

  const articles = useMemo(() => {
    const VoucherChip = ({
      voucher,
      jubilee = false,
    }: {
      voucher: { discount: string; validUntil: string };
      jubilee?: boolean;
    }) =>
      isValidVoucher(voucher?.validUntil) ? (
        <div className="flex flex-col gap-2">
          <Typography weight="semibold" className="mr-6 text-sm" as="h3">
            <span>{jubilee ? t('club.jubilee') : t('club.offer')}</span>
          </Typography>
          <div className="flex">
            <div
              className={cx(
                'rounded-md px-2  ',
                jubilee
                  ? 'bg-jubilee font-semibold text-type-heading '
                  : 'bg-primary-800 text-white '
              )}
            >
              <span>{voucher.discount}</span>
            </div>
          </div>
        </div>
      ) : null;

    return pipe(
      items,
      fromPredicate(Array.isArray),
      map(
        mapA((item) =>
          pipe(
            item,
            fromPredicate(
              (x): x is ClubOfferListItem => x.__typename === 'ClubOffer'
            ),
            map(({ sys, title, images, category, location }) => (
              <DefaultLink
                href={getLink(sys.id)?.path || ''}
                className="!no-underline"
                color={'neutral'}
                key={sys.id}
              >
                <Card.Root
                  key={sys.id}
                  className="group relative flex h-full flex-col-reverse justify-end !bg-neutral-100 transition ease-in-out hover:!shadow-lg"
                  as="article"
                >
                  <Card.Header className="row-start-2 flex h-[60%] flex-col justify-between !pt-10">
                    <div>
                      <CategoryAndLocation
                        category={category}
                        location={location}
                      />

                      <Typography
                        as="h2"
                        intent="title"
                        className="text-balance leading-8 text-black decoration-transparent transition-all group-hover:underline group-hover:decoration-inherit"
                        weight="semibold"
                      >
                        {title}
                      </Typography>
                    </div>
                    <div
                      className={cx([
                        'flex',
                        'items-end',
                        isValidVoucher(item.voucher?.validUntil) ||
                        isValidVoucher(item.jubileeVoucher?.validUntil)
                          ? 'border-t-2 border-gray-300'
                          : '',
                        'pt-6',
                      ])}
                    >
                      <VoucherChip voucher={item.voucher} />
                      <VoucherChip
                        voucher={item.jubileeVoucher}
                        jubilee={true}
                      />
                    </div>
                  </Card.Header>

                  <Card.Content className="relative h-64 !px-0">
                    {pipe(
                      images,
                      head,
                      map((img) => (
                        <div className="-mx-4">
                          <Image
                            key={img.sys.id}
                            className={cx(
                              item.jubileeVoucher &&
                                'rounded-2xl border-4 border-solid border-jubilee'
                            )}
                            alt={img.description || ''}
                            src={img.url}
                            sizes="(max-width: 768px) 50vw, 20vw"
                            quality={50}
                            fill
                            style={{
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                      )),
                      toUndefined
                    )}
                    {item.jubileeVoucher && (
                      <div className="absolute bottom-0 left-0 right-0 translate-y-1/2">
                        <JubileeBow className="relative w-full" />
                      </div>
                    )}
                  </Card.Content>
                </Card.Root>
              </DefaultLink>
            )),
            getOrElse(() =>
              pipe(item as ClubGuideListItem, ({ sys, title }) => (
                <DefaultLink
                  href={getLink(sys.id)?.path || ''}
                  full
                  color={'white'}
                  className="min-h-96 !no-underline"
                  key={sys.id}
                >
                  <Card.Root
                    key={sys.id}
                    className="group flex h-full flex-col-reverse transition ease-in-out hover:!shadow-lg "
                    as="article"
                  >
                    <Card.Content className="relative flex h-full flex-col items-center justify-center gap-2 bg-purple-800">
                      <div className="z-1 z-10 rounded-md bg-primary-900 px-2 py-1  text-white">
                        <span className="!z-1 text-sm uppercase">
                          {t('club.guide')}
                        </span>
                      </div>
                      <Typography
                        as="h2"
                        intent="title"
                        size="lg"
                        color="white"
                        className="z-10 text-center group-hover:underline "
                      >
                        {title}
                      </Typography>
                      {item.images[0] && (
                        <Image
                          className="absolute left-0 top-0 opacity-15"
                          alt={item.images[0].description || ''}
                          src={item.images[0].url}
                          sizes="(max-width: 768px) 50vw,
                            20vw"
                          quality={100}
                          fill
                          style={{
                            objectFit: 'cover',
                            aspectRatio: '16/9',
                          }}
                        />
                      )}
                    </Card.Content>
                  </Card.Root>
                </DefaultLink>
              ))
            )
          )
        )
      ),
      toUndefined
    );
  }, [items, t, getLink]);

  const LocationFilter = () => {
    return locations ? (
      <div>
        <Typography as="h3" className="text-sm font-semibold">
          {t('club.filter.locationLabel')}
        </Typography>

        <PageFilter.Root className="flex gap-2 pb-6 pt-2">
          {locations.map((filter) => (
            <PageFilter.Button
              key={`key_${filter.value}`}
              active={
                (currentCountryParam === undefined &&
                  filter.value === _showAllValue) ||
                currentCountryParam === filter.value
              }
              version={2}
              filterKey={filter.value}
              label={filter.label}
              onFilterChange={() => handleCountryChange(filter.value)}
            />
          ))}
        </PageFilter.Root>
      </div>
    ) : null;
  };

  const CategoryFilter = () => (
    <>
      <div className="">
        <Typography as="h3" className="text-sm font-semibold">
          {t('club.filter.categoryLabel')}
        </Typography>

        <PageFilter.Root className="flex gap-2 overflow-x-auto pt-2 sm:flex-wrap">
          {filters.map((filter) => (
            <PageFilter.Button
              key={filter.key}
              active={
                (currentCategoryParam === undefined &&
                  filter.key === _showAllValue) ||
                currentCategoryParam === filter.key
              }
              version={2}
              filterKey={filter.key}
              label={filter.label}
              count={filter.count}
              onFilterChange={() => handleFilterChange(filter.key)}
            />
          ))}
        </PageFilter.Root>
      </div>
    </>
  );

  return (
    <Grid data-mode="club">
      <Typography
        as="h2"
        intent="headline"
        size="xl2"
        weight="semibold"
        className="col-span-full my-1"
      >
        {t('club.ourClubOffers')}
      </Typography>
      <div className="col-span-full gap-8 md:flex">
        <LocationFilter />
        <CategoryFilter />
      </div>

      <Box
        className={cx([
          'relative',
          'grid',
          'grid-cols-1',
          'sm:grid-cols-2',
          'md:grid-cols-3',
          'col-span-full',
          'auto-rows-[1fr]',
          'gap-6',
          'mt-6',
        ])}
      >
        {!isFetching ? articles : <LoadingArticles />}
      </Box>

      <Box className="col-span-full mt-8 flex justify-center">
        <Pagination
          data-mode="club"
          count={pages}
          current={currentPageParam || 1}
          siblings={isMobile ? 0 : undefined}
          boundary={isMobile ? 0 : undefined}
          className="sm:flex"
          nextLabel={t('pagination.next')}
          prevLabel={t('pagination.previous')}
          goToLabel={(nr) => t('pagination.goTo', { nr })}
        />
      </Box>
    </Grid>
  );
};
